import React, { useEffect, useState } from "react";
import {
  Box,
  CssBaseline,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";

import SelexSolNavbar from "./CommonC/SelexSolNavbar";

import GettingStarted from "./GettingStarted";
import MachineLearning from "./SelexHelp/MachineLearning";
import ImportData from "./ImportData";
import KeyIndicatorsStep from "./KeyIndicatorsStep";
import DataReconditioningStep from "./DataReconditioningStep";
import ModelBuildingStep from "./ModelBuildingStep";
import ModelValidationContent from "./ModelValidationContent";
import ModelDeploymentContent from "./ModelDeploymentContent";

const primary = "#0078c9";
const accent = "#9e0b22";
const navy = "#061d34";

function Help() {
  const location = useLocation();
  const [activeLink, setActiveLink] = useState("");

  const links = [
    { text: "Getting Started", to: "#gettingstarted" },
    { text: "Import Data", to: "#import" },
    { text: "Key Indicators Definition", to: "#kpi" },
    { text: "Data Reconditioning", to: "#data" },
    { text: "Model Building and Algorithm Selection", to: "#model" },
    { text: "Model Validation", to: "#validation" },
    { text: "Model Deployment", to: "#deployment" },
    { text: "Machine Learning Guidelines", to: "#ml" },
  ];

  useEffect(() => {
    setActiveLink(location.hash || "#gettingstarted");
  }, [location.hash]);

  const renderContent = () => {
    switch (activeLink) {
      case "#import":
        return <ImportData />;
      case "#kpi":
        return <KeyIndicatorsStep />;
      case "#data":
        return <DataReconditioningStep />;
      case "#model":
        return <ModelBuildingStep />;
      case "#validation":
        return <ModelValidationContent />;
      case "#deployment":
        return <ModelDeploymentContent />;
      case "#ml":
        return <MachineLearning />;
      case "#gettingstarted":
      default:
        return <GettingStarted />;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 8% 10%, rgba(0,120,201,0.08), transparent 28%), radial-gradient(circle at 90% 12%, rgba(158,11,34,0.07), transparent 25%), #fff",
        overflowX: "hidden",
      }}
    >
      <CssBaseline />

      <SelexSolNavbar isLoggedIn={!!sessionStorage.getItem("usertoken")} />

      <Box
        sx={{
          width: "min(1320px, calc(100% - 32px))",
          mx: "auto",
          pt: { xs: 2, md: 3 },
          pb: 6,
        }}
      >
        {/* PAGE HEADER */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Chip
            label="Help Center"
            sx={{
              bgcolor: "rgba(158,11,34,0.08)",
              color: accent,
              fontWeight: 900,
              fontSize:14,
              letterSpacing: 0.5,
              mb: 1.5,
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: 30, md: 46 },
              fontWeight: 950,
              color: "#0078c9",
              letterSpacing: "-1.5px",
              mb: 1.5,
            }}
          >
            SelexMB™ User Guide
          </Typography>

          <Typography
            sx={{
              color: "#64748b",
              maxWidth: 760,
              mx: "auto",
              lineHeight: 1.75,
              fontSize: 15,
            }}
          >
            Learn how to import data, define indicators, build models,
            validate results, deploy workflows, and follow machine learning
            best practices.
          </Typography>
        </Box>

        {/* LAYOUT */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "310px 1fr" },
            gap: 3,
            alignItems: "start",
          }}
        >
          {/* SIDEBAR */}
          <Box
            sx={{
              position: { xs: "relative", md: "sticky" },
              top: { md: 105 },
              borderRadius: "30px",
              background: "rgba(255,255,255,0.94)",
              border: "1px solid rgba(0,120,201,0.13)",
              boxShadow: "0 18px 50px rgba(6,29,52,0.08)",
              overflow: "hidden",
              p: 2,
            }}
          >
            <Typography
              sx={{
                px: 1.5,
                py: 1,
                fontSize: 13,
                fontWeight: 950,
                color: accent,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Documentation
            </Typography>

            <List sx={{ display: "grid", gap: 1 }}>
              {links.map((link, index) => {
                const active = activeLink === link.to;

                return (
                  <ListItemButton
                    key={link.text}
                    component={Link}
                    to={link.to}
                    selected={active}
                    sx={{
                      borderRadius: "18px",
                      px: 2,
                      py: 1.3,
                      border: active
                        ? `1px solid ${primary}`
                        : "1px solid transparent",
                      background: active
                        ? "rgba(0,120,201,0.10)"
                        : "transparent",
                      color: active ? primary : navy,
                      transition: "0.25s",
                      "&:hover": {
                        background: "rgba(0,120,201,0.08)",
                        color: primary,
                      },
                      "&.Mui-selected": {
                        background: "rgba(0,120,201,0.10)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        mr: 1.5,
                        borderRadius: "11px",
                        display: "grid",
                        placeItems: "center",
                        fontSize: 12,
                        fontWeight: 900,
                        color: active ? "#fff" : primary,
                        background: active
                          ? `linear-gradient(135deg, ${primary}, ${accent})`
                          : "rgba(0,120,201,0.08)",
                      }}
                    >
                      {index + 1}
                    </Box>

                    <ListItemText
                      primary={link.text}
                      primaryTypographyProps={{
                        fontSize: 13,
                        fontWeight: 850,
                        lineHeight: 1.35,
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>

          {/* CONTENT */}
          <Box
            sx={{
              borderRadius: "34px",
              background: "#fff",
              border: "1px solid rgba(0,120,201,0.13)",
              boxShadow: "0 18px 50px rgba(6,29,52,0.08)",
              p: { xs: 2.5, md: 4 },
              minHeight: "70vh",
              overflow: "hidden",
              "& h1, & h2, & h3": {
                color: "#0078c9",
                fontWeight: 900,
              },
              "& p": {
                color: "#475569",
                lineHeight: 1.8,
                fontSize: "14px",
              },
              "& img": {
                maxWidth: "100%",
                borderRadius: "18px",
              },
            }}
          >
            {renderContent()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Help;